/**
 * Video QA Studio v2 — the tracking wizard's review surface.
 *
 * WHAT IS REAL AND WHAT IS NOT, because that is the only thing about this
 * page anybody needs to hold in their head:
 *
 *   REAL   the file list and the video, read from R2's
 *          engineering-2026/examples/video-tagger/ through this API, played
 *          from a short-lived presigned URL.
 *   SAMPLE everything else — boxes, tracks, scores, suspicions, the setup
 *          guide's progress, the calibration solve. It comes from
 *          videoStudio/showcase.ts and is transcribed from a real wizard
 *          session, because that folder holds video and reference frames and
 *          no tracking data at all.
 *
 * The page SAYS SO, on screen, in a band that does not scroll away. An
 * interface that shows 29,220 detections and 45 tracks over a real match is
 * indistinguishable from a working product, and somebody will screenshot it.
 * The band is what stops that being our fault.
 *
 * THE COPY IS THE WIZARD'S OWN, verbatim (web/src/guide.jsx, panels.jsx,
 * api.js). Showcasing a paraphrase would be showcasing something we have not
 * built.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { API_BASE, ApiError, api } from '../lib/api'
import {
  CALIBRATION, GROUND_TRUTH, JOBS, REGISTRATION, REGISTRATION_COLOR,
  RUN, SCORES, STEPS, SUSPICIONS, SUSPICION_TOTAL, TABS, TAB_LABEL,
  TEAM_COLOR, TRACKS, TRACK_COUNTS, type Tab,
} from './videoStudio/showcase'
import './qa-studio-v2.css'

type Asset = { name: string; size: number; kind: 'video' | 'image' | 'other'; last_modified: string | null }
type Listing = { bucket: string; prefix: string; assets: Asset[] }

/** One detection, positional as the API sends it. */
type Row = [number, number, number, number, number, string | null, string | null, number]
type Detections = {
  run: number; fps: number; width: number; height: number; frames: number
  boxes: Row[]
}

/** Boxes grouped by frame, built once so the draw loop is a lookup. */
function byFrame(d: Detections | null) {
  const map = new Map<number, Row[]>()
  if (!d) return map
  for (const r of d.boxes) {
    const list = map.get(r[0])
    if (list) list.push(r)
    else map.set(r[0], [r])
  }
  return map
}

/** Team from the wizard's label prefix — 'TEN-A' -> TEN, 'GK-B' -> GK. */
const teamOf = (label: string | null) => (label ?? '').split('-')[0] || 'SPEC'

const mb = (b: number) =>
  b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`

const assetHref = (name: string) =>
  `${API_BASE}/api/v1/qa-studio-v2/asset/${encodeURIComponent(name)}`

const OVERLAYS = [
  ['boxes', 'boxes'], ['labels', 'labels'], ['jersey', 'jersey #'],
  ['confidence', 'confidence'], ['offpitch', 'off-pitch'],
  ['gt', 'ground truth'], ['raw', 'raw positions'],
] as const
type OverlayKey = (typeof OVERLAYS)[number][0]

export default function QaStudioV2() {
  const [listing, setListing] = useState<Listing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('wizard')
  const [nonce, setNonce] = useState(0)
  const [dets, setDets] = useState<Detections | null>(null)
  const [detsError, setDetsError] = useState<string | null>(null)
  const [frame, setFrame] = useState(0)
  const [time, setTime] = useState(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const framesRef = useRef<Map<number, Row[]>>(new Map())
  const [overlays, setOverlays] = useState<Record<OverlayKey, boolean>>({
    boxes: true, labels: true, jersey: true,
    confidence: false, offpitch: false, gt: true, raw: false,
  })

  const load = useCallback(() => {
    setError(null)
    api<Listing>('/api/v1/qa-studio-v2/assets')
      .then((data) => {
        setListing(data)
        setSelected((cur) => cur ?? data.assets.find((a) => a.kind === 'video')?.name
          ?? data.assets[0]?.name ?? null)
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
  }, [])
  useEffect(load, [load])

  useEffect(() => {
    api<Detections>('/api/v1/qa-studio-v2/detections')
      .then((d) => { framesRef.current = byFrame(d); setDets(d); setDetsError(null) })
      .catch((err: unknown) => {
        setDets(null)
        framesRef.current = new Map()
        setDetsError(err instanceof ApiError ? err.message : String(err))
      })
  }, [])

  // THE DRAW LOOP. The frame is derived from the video's own clock —
  // currentTime * fps — exactly as the wizard does it, because the element is
  // the only thing that knows where playback actually is after a seek, a
  // stall or a dropped frame. requestAnimationFrame rather than a timer so it
  // repaints with the compositor instead of fighting it.
  useEffect(() => {
    if (!dets) return
    let raf = 0
    const tick = () => {
      const v = videoRef.current
      if (v) {
        setTime(v.currentTime)
        setFrame(Math.min(dets.frames - 1, Math.floor(v.currentTime * dets.fps)))
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [dets])

  const current = listing?.assets.find((a) => a.name === selected) ?? null
  const src = current ? `${assetHref(current.name)}?v=${nonce}` : null
  const toggle = (k: OverlayKey) => setOverlays((o) => ({ ...o, [k]: !o[k] }))

  return (
    <div className="qav2">
      <header className="qav2-head">
        <h1>priori</h1>
        <span className="qav2-ready" title="how much of the setup for this clip is done">
          <span className="qav2-bar"><i style={{ width: `${RUN.readyPercent}%` }} /></span>
          {RUN.readyPercent}% ready
        </span>
        <span className="qav2-dim">
          {RUN.video} · run {RUN.runId} · {RUN.frames}f @ {RUN.fps}fps ·{' '}
          {RUN.detections.toLocaleString()} dets · {RUN.tracks} tracks
        </span>
        <span className="qav2-ok">
          calibrated: {RUN.calibration.points} pts, {RUN.calibration.reprojErrM} m,
          pitch {RUN.calibration.lengthM}×{RUN.calibration.widthM} m
        </span>
        {error && <span className="qav2-err">{error}</span>}
      </header>

      {/* Does not scroll away, deliberately. Its wording follows the data:
          claiming "sample" over real detections would be as wrong as the
          reverse. */}
      <div className={`qav2-band${dets ? ' qav2-band--live' : ''}`}>
        {dets ? (
          <>
            <strong>Live.</strong> The clip and the {dets.boxes.length.toLocaleString()}{' '}
            boxes are both real — video and{' '}
            <code>priori.sqlite</code> read from{' '}
            <code>{listing?.bucket}/{listing?.prefix}</code>, run {dets.run} at{' '}
            {dets.fps.toFixed(2)} fps. The side panels are still sample data.
          </>
        ) : (
          <>
            <strong>Showcase.</strong> The clip and the file list are real, read
            from{' '}
            <code>{listing?.bucket ?? 'R2'}/{listing?.prefix ?? 'engineering-2026/examples/video-tagger/'}</code>.
            No boxes are drawn: {detsError ?? 'the detections are still loading'}.
            Every number in the side panels is sample data.
          </>
        )}
      </div>

      <main className="qav2-main">
        <div className="qav2-stagecol">
          <div className="qav2-stage">
            {current?.kind === 'video' && src && (
              <video ref={videoRef} key={src} src={src} controls preload="metadata"
                     onError={() => setError('Playback failed — the signed link may have expired. Try re-sign.')} />
            )}
            {current?.kind === 'image' && src && <img src={src} alt={current.name} />}
            {!current && <div className="qav2-empty">Nothing selected.</div>}

            <div className="qav2-frameid">
              frame {frame}&nbsp;&nbsp;
              {Math.floor(time / 60)}:{(time % 60).toFixed(2).padStart(5, '0')}
            </div>

            {overlays.boxes && current?.kind === 'video' && dets && (
              <div className="qav2-boxes" aria-hidden>
                {(framesRef.current.get(frame) ?? []).map((b, i) => {
                  const [, x1, y1, x2, y2, label, , onPitch] = b
                  if (!overlays.offpitch && !onPitch) return null
                  const team = teamOf(label)
                  // Percentages of the VIDEO'S OWN pixel grid. The element is
                  // width:100% with auto height, so it renders the whole frame
                  // with no crop — which makes a percentage the same point at
                  // any window size. Absolute pixels would need a resize
                  // observer and would still be wrong for one paint after it.
                  return (
                    <div key={`${label}-${i}`} className="qav2-box"
                         style={{
                           left: `${(x1 / dets.width) * 100}%`,
                           top: `${(y1 / dets.height) * 100}%`,
                           width: `${((x2 - x1) / dets.width) * 100}%`,
                           height: `${((y2 - y1) / dets.height) * 100}%`,
                           borderColor: TEAM_COLOR[team] ?? '#9aa4b6',
                         }}>
                      {overlays.labels && label && (
                        <span className="qav2-boxlabel"
                              style={{ background: TEAM_COLOR[team] ?? '#9aa4b6' }}>
                          {label}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="qav2-timeline" aria-hidden>
            {REGISTRATION.map((r, i) => (
              <i key={i} style={{
                left: `${r.from}%`, width: `${r.to - r.from}%`,
                background: REGISTRATION_COLOR[r.kind],
              }} />
            ))}
          </div>
          <div className="qav2-legend">
            <span className="qav2-dim">registration:</span>
            {Object.entries(REGISTRATION_COLOR).map(([kind, color]) => (
              <span key={kind} className="qav2-key">
                <i style={{ background: color }} />{kind}
              </span>
            ))}
          </div>

          <div className="qav2-strip">
            {current && <><span className="qav2-dim">{current.name}</span>
              <span className="qav2-dim">{mb(current.size)}</span></>}
            <span className="qav2-spacer" />
            <button className="qav2-btn qav2-btn--small" onClick={() => setNonce((n) => n + 1)}>
              re-sign
            </button>
            <button className="qav2-btn qav2-btn--small" onClick={load}>reload</button>
          </div>

          <div className="qav2-toggles">
            {OVERLAYS.map(([k, label]) => (
              <label key={k} className="qav2-toggle">
                <input type="checkbox" checked={overlays[k]} onChange={() => toggle(k)} />{' '}
                {label}
              </label>
            ))}
          </div>
        </div>

        <aside className="qav2-aside">
          <div className="qav2-card">
            <div className="qav2-tabs">
              {TABS.map((t) => (
                <button key={t} className={`qav2-btn${tab === t ? ' qav2-btn--active' : ''}`}
                        onClick={() => setTab(t)}>{TAB_LABEL[t]}</button>
              ))}
            </div>
            <Panel tab={tab} listing={listing} selected={selected} onSelect={setSelected} />
          </div>

          <div className="qav2-card qav2-card--tracks">
            <h2>
              Tracks ({TRACK_COUNTS.participants} participants) ·{' '}
              <span className="qav2-dim">{TRACK_COUNTS.rejected} spectators/rejected</span>
            </h2>
            <table className="qav2-table">
              <thead><tr><th>label</th><th>team</th><th>conf</th><th>off%</th><th>frames</th></tr></thead>
              <tbody>
                {TRACKS.map((t) => (
                  <tr key={t.label}>
                    <td><span className="qav2-chip" style={{ background: TEAM_COLOR[t.team] }}>{t.label}</span></td>
                    <td className="qav2-dim">{t.team}</td>
                    <td className="qav2-dim">{t.conf.toFixed(2)}</td>
                    <td className="qav2-dim">{t.offPct}</td>
                    <td className="qav2-dim">{t.frames}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </aside>
      </main>
    </div>
  )
}

function Panel({ tab, listing, selected, onSelect }: {
  tab: Tab; listing: Listing | null; selected: string | null
  onSelect: (name: string) => void
}) {
  if (tab === 'wizard') return <SetupGuide />
  if (tab === 'cleanup') return <FixUps />
  if (tab === 'calibrate') return <MarkField />
  if (tab === 'label' || tab === 'annotate') return <GroundTruth />
  if (tab === 'stats') return <Scores />
  if (tab === 'jobs') return <Jobs />
  if (tab === 'videos') return <Videos listing={listing} selected={selected} onSelect={onSelect} />
  return (
    <div>
      <h2>{TAB_LABEL[tab]}</h2>
      <p className="qav2-hint">
        In the wizard this is the 2D minimap and the playback controls, driven
        by per-frame positions. There are none in the sample folder, so there is
        nothing honest to draw here.
      </p>
    </div>
  )
}

function SetupGuide() {
  const step = STEPS.find((s) => s.state === 'current')!
  const done = STEPS.filter((s) => s.state === 'done').length
  return (
    <div>
      <h2>Setup guide</h2>
      <div className="qav2-steps" aria-hidden>
        {STEPS.map((s) => <i key={s.n} className={`qav2-step qav2-step--${s.state}`} />)}
      </div>
      <p className="qav2-dim">Step {step.n} of {STEPS.length} · {done} finished</p>

      <div className="qav2-guide">
        <div className="qav2-guide__head">
          <span className="qav2-guide__n">{step.n}</span>
          <div>
            <h3>{step.title}</h3>
            <span className="qav2-dim">{step.mins}</span>
          </div>
        </div>
        <p>
          Compares what the machine found against the frames you just checked,
          and prints how often it was right. Free to re-run as often as you like.
        </p>
        <div className="qav2-readout">Never measured on this clip.</div>
        <button className="qav2-btn qav2-btn--primary" disabled
                title="Sample surface — there is no evaluate job behind this">
          Score it now →
        </button>
        <p className="qav2-do"><strong>Do this:</strong></p>
        <ol className="qav2-ol">
          <li>Press <code>Score it now</code> below.</li>
          <li>Ten seconds later the numbers appear here and in the <code>Stats</code> tab.</li>
          <li>Three numbers matter: <strong>found</strong> (how many real people it
            caught), <strong>correct</strong> (how many of its boxes were really
            people), and <strong>teams</strong> (how often it named the right side).</li>
        </ol>
        <p className="qav2-verify">
          <strong>You will know it worked when:</strong> This card stops saying
          "never measured" and shows three percentages.
        </p>
        <div className="qav2-guide__foot">
          <button className="qav2-btn qav2-btn--small" disabled>‹ back</button>
          <button className="qav2-btn qav2-btn--small" disabled>skip for now</button>
          <span className="qav2-spacer" />
          <button className="qav2-btn qav2-btn--small" disabled>next ›</button>
        </div>
      </div>
      <ul className="qav2-steplist">
        {STEPS.map((s) => (
          <li key={s.n} className={`qav2-steprow qav2-steprow--${s.state}`}>
            <span className="qav2-dim">{s.n}</span> {s.title}
            <span className="qav2-spacer" />
            <span className="qav2-dim">{s.mins}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function FixUps() {
  return (
    <div>
      <h2>Suggested cleanup {SUSPICION_TOTAL} suspicions · run {RUN.runId}</h2>
      <p className="qav2-hint">
        Ranked by how unsure the app is. <strong>show me →</strong> spotlights the
        exact thing in question on the video at the left and explains it here.
        Settled rows drop off the list; re-run afterwards to apply your fixes
        across the whole clip.
      </p>
      {SUSPICIONS.map((s, i) => (
        <div key={i} className="qav2-susp">
          <span className="qav2-bang">!</span>
          <p>
            <strong>{s.label}</strong> {s.why} <span className="qav2-dim">@ f{s.frame}</span>
          </p>
          <button className="qav2-btn qav2-btn--small" disabled>show me →</button>
        </div>
      ))}
      <button className="qav2-btn qav2-btn--small" disabled>↻ refresh</button>
    </div>
  )
}

function MarkField() {
  return (
    <div>
      <h2>Mark the field</h2>
      <p className="qav2-hint">
        Pick a spot from the list, then click it on the picture. Do 6–10 of them
        spread right across the field, add points along the four boundary lines,
        and press Solve. That is what turns pixels into metres.
      </p>
      <div className="qav2-pitch">
        <span className="qav2-pitch__far">FAR touchline (top of video)</span>
        <svg viewBox="0 0 320 180" role="img" aria-label="Pitch diagram">
          <rect x="10" y="14" width="300" height="152" />
          <line x1="160" y1="14" x2="160" y2="166" />
          <circle cx="160" cy="90" r="26" />
          <rect x="10" y="46" width="46" height="88" />
          <rect x="264" y="46" width="46" height="88" />
          <rect x="10" y="70" width="14" height="40" />
          <rect x="296" y="70" width="14" height="40" />
        </svg>
        <span className="qav2-pitch__near">NEAR touchline (bottom, camera side)</span>
      </div>
      <p className="qav2-hint">
        <strong>Boundary lines</strong> — click points ALONG each painted line.
        They pin the fit where landmarks are sparse (the far-line bulge) and
        measure the panorama's curvature.
      </p>
      <div className="qav2-bounds">
        {CALIBRATION.boundaries.map((b) => (
          <span key={b.name} className="qav2-bound">{b.name} · {b.count}</span>
        ))}
      </div>
      <p className="qav2-hint">
        Strongest anchors: halfway-line/circle tangents, penalty-area corners,
        goal posts. Avoid the circle's left/right extremes (~2.5 m ellipse bias,
        measured). Re-clicking a landmark replaces it; ✕ removes.
      </p>
      <table className="qav2-table">
        <tbody>
          {CALIBRATION.landmarks.map((l) => (
            <tr key={l.name}>
              <td>{l.name}</td>
              <td className={l.placed ? 'qav2-ok' : 'qav2-dim'}>
                {l.placed ? `✓ ${l.at}` : l.at}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function GroundTruth() {
  return (
    <div>
      <h2>Ground truth — you are the sensor</h2>
      <p className="qav2-hint">
        Pause, pick a team, <strong>drag a box over each person</strong> (head to
        feet, tight). ⌥-click a box to delete it. Label frames{' '}
        <strong>completely</strong> — every player, both teams, officials — or
        recall numbers will lie. Spread ~10-20 frames across the clip, then run
        Evaluate on the Jobs tab.
      </p>
      <div className="qav2-teams">
        {GROUND_TRUTH.teams.map((t) => (
          <button key={t} className="qav2-teambtn" disabled
                  style={{ borderColor: TEAM_COLOR[t] ?? '#8a94a6', color: TEAM_COLOR[t] ?? '#8a94a6' }}>
            {t}
          </button>
        ))}
      </div>
      <p className="qav2-hint">
        <strong>SPEC</strong> = spectator/ball-crew/bench: draw it over people who
        are NOT participants. If the system presents one as a player, that counts
        as an error.
      </p>
      <div className="qav2-kv">
        <span>complete frames</span>
        <span className="qav2-ok">
          {GROUND_TRUTH.completeFrames.length} ({GROUND_TRUTH.completeFrames.join(', ')})
        </span>
        <span>partial frames</span><span className="qav2-dim">—</span>
        <span>boxes this frame</span><span className="qav2-dim">{GROUND_TRUTH.boxesThisFrame}</span>
      </div>
      <button className="qav2-btn qav2-btn--primary" disabled>✓ mark frame complete</button>
      <p className="qav2-hint">
        Only frames marked <strong>complete</strong> are scored by Evaluate — the
        mark is your statement that <strong>every person in the frame is
        boxed</strong>. Partial frames are stored but never scored.
      </p>
      <div className="qav2-tabs">
        <button className="qav2-btn qav2-btn--small" disabled>◀ labelled</button>
        <button className="qav2-btn qav2-btn--small" disabled>labelled ▶</button>
        <button className="qav2-btn qav2-btn--small" disabled>clear frame</button>
      </div>
    </div>
  )
}

function Scores() {
  return (
    <div>
      <h2>Scores</h2>
      {!SCORES.measured && <div className="qav2-readout">Never measured on this clip.</div>}
      <p className="qav2-hint">
        Three numbers matter: <strong>found</strong> (how many real people it
        caught), <strong>correct</strong> (how many of its boxes were really
        people), and <strong>teams</strong> (how often it named the right side).
      </p>
    </div>
  )
}

function Jobs() {
  return (
    <div>
      <h2>Jobs</h2>
      <table className="qav2-table">
        <thead><tr><th>#</th><th>kind</th><th>status</th><th>took</th></tr></thead>
        <tbody>
          {JOBS.map((j) => (
            <tr key={j.id}>
              <td>{j.id}</td>
              <td className="qav2-dim">{j.kind}</td>
              <td className={j.status === 'failed' ? 'qav2-err' : 'qav2-ok'}>{j.status}</td>
              <td className="qav2-dim">{j.took}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Videos({ listing, selected, onSelect }: {
  listing: Listing | null; selected: string | null; onSelect: (n: string) => void
}) {
  return (
    <div>
      <h2>Videos</h2>
      <p className="qav2-hint">
        The one genuinely live panel: these are the objects in the R2 sample
        folder. Selecting one plays it from a fresh presigned link.
      </p>
      <table className="qav2-table">
        <thead><tr><th>name</th><th>kind</th><th>size</th></tr></thead>
        <tbody>
          {(listing?.assets ?? []).map((a) => (
            <tr key={a.name}
                className={`qav2-row${a.name === selected ? ' qav2-row--sel' : ''}`}
                onClick={() => onSelect(a.name)}>
              <td>{a.name}</td>
              <td className="qav2-dim">{a.kind}</td>
              <td className="qav2-dim">{mb(a.size)}</td>
            </tr>
          ))}
          {listing && listing.assets.length === 0 && (
            <tr><td colSpan={3} className="qav2-dim">The folder is empty.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
